import { create } from "zustand/react";
import { JsonParse } from "@/helpers/json.ts";

export enum SettingContentTooltip {
  AudioChatReply = "Enabling this setting will allow audio to play when student messages you while they are not the active chat",
  AudioChatAlways = "Allows audio to play regardless of what chat is opened",
  AudioNewSession = "Enabling this setting will allow audio to play when a new session is assigned to you",

  MiscSignalRDebug = "Sets SignalR logging to debug, otherwise defaults to error. Only enable this if you know what you are doing",
  MiscRefreshTimer = "Sets the refresh time before all chat gets refetched. Lowering this will increase the amount of requests made to the server, but may improve responsiveness",
  MiscDisableTooltips = "Disables tooltips (if you find them annoying)",

  AdminEnabled = "Enables admin mode, which allows access to additional features that may not be useful for tutors",

  FontSizeOffset = "Adjusts the font sizing",
  ChatContextPopout = "Makes chat context popout into a new window instead of shifting content to the left",
  SmallHeight = "Enabling this will reduce height of a lot of elements for devices with smaller height",

  MessagePack = "Enabling this will will attempt to connect to SignalR using the MessagePack protocol. This should improve performance, as its faster to decode/encode, along with better compression (up to 50% less bytes transmitted). Very experimental, expect bugs/issues.",
}

export enum SettingContent {
  AudioChatReply = "On every new message",
  AudioChatAlways = "Including active chat?",
  AudioNewSession = "On new assigned session",

  MiscSignalRDebug = "Debug SignalR events",
  MiscRefreshTimer = "All chat cooldown",
  MiscDisableTooltips = "Disable Tooltips",

  AdminEnabled = "Admin mode",

  FontSizeOffset = "Font offset",
  ChatContextPopout = "Chat context popout",
  SmallHeight = "Small screen height",

  MessagePack = "Enable MessagePack handshake",
}

export interface Settings {
  AudioChatBlip: boolean;
  AudioChatAlways: boolean;
  AudioNewSession: boolean;

  MiscSignalRDebug: boolean;
  MiscRefreshTimer: number;
  MiscDisableTooltips: boolean;

  AdminEnabled: boolean;

  FontSizeOffset: number;
  ChatContextPopout: boolean;
  SmallHeight: boolean;

  MessagePack: boolean;
}

export type SettingStore = {
  settings: Settings;
  setSettings: (newSettings: Settings) => void;
  fetchSettings: () => void;
};

const SETTINGS_KEY = "eedi-tutor-admin-settings";

const DefaultSettings: Settings = Object.freeze({
  AudioChatBlip: true,
  AudioChatAlways: false,
  AudioNewSession: true,
  MiscSignalRDebug: false,
  MiscRefreshTimer: 10,
  MiscDisableTooltips: false,
  AdminEnabled: false,
  FontSizeOffset: 0,
  ChatContextPopout: false,
  SmallHeight: false,
  MessagePack: false,
});

export const useSettingsStore = create<SettingStore>((set) => ({
  settings: { ...DefaultSettings },
  setSettings: (newSettings) => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(newSettings));
    set({ settings: newSettings });
  },
  fetchSettings: () => {
    const stored = localStorage.getItem(SETTINGS_KEY);

    if (!stored) {
      return;
    }

    try {
      const defaultSetting = { ...DefaultSettings };

      const partialJson = JsonParse<Partial<Settings>>(stored);
      Object.assign(defaultSetting, partialJson);

      set({ settings: defaultSetting });
    } catch {
      localStorage.removeItem(SETTINGS_KEY);
    }
  },
}));
